import { AlertOctagon, AlertTriangle, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useLocalization } from "@/context/localization-context";
import type { PendingChange } from "../model";
import type { SettingText } from "../use-setting-text";

/**
 * Confirmation before the edits go to production: every change as old -> new (with its effect on the
 * pizzas of the histogram window where there is one), then the save itself.
 */
export function SaveDialog({
  open,
  onOpenChange,
  changes,
  impact,
  saving,
  error,
  onConfirm,
  text,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  changes: PendingChange[];
  /** "Last 7 days: 375 → 412 pizzas (+1.2 pp)", or null */
  impact: (change: PendingChange) => string | null;
  saving: boolean;
  error: string | null;
  onConfirm: () => void;
  text: SettingText;
}) {
  const { t } = useLocalization();
  return (
    <Dialog open={open} onOpenChange={(next) => !saving && onOpenChange(next)}>
      <DialogContent className="max-w-xl gap-5">
        <DialogHeader className="text-left">
          <DialogTitle className="flex items-center gap-2 pr-6">
            <AlertTriangle className="h-5 w-5 shrink-0 text-amber-400" aria-hidden />
            {t("evalSettings.confirm.title")}
          </DialogTitle>
          <DialogDescription>{t("evalSettings.confirm.description")}</DialogDescription>
        </DialogHeader>

        <ul className="divide-y divide-border/70 rounded-md border">
          {changes.map((change) => {
            const effect = impact(change);
            return (
              <li key={change.setting.key} className="flex min-w-0 flex-col gap-1 px-3 py-2.5">
                <div className="flex min-w-0 flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                  <span className="text-sm font-medium">{text.label(change.setting.key)}</span>
                  <code className="break-all font-mono text-[11px] text-muted-foreground">{change.setting.key}</code>
                </div>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm tabular-nums">
                  <span className="text-muted-foreground">{text.value(change.setting.key, change.old)}</span>
                  <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
                  <span className="font-semibold">{text.value(change.setting.key, change.next)}</span>
                  {change.toDefault && (
                    <span className="inline-flex h-5 items-center rounded-md border px-1.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                      {t("evalSettings.confirm.default")}
                    </span>
                  )}
                </div>
                {effect && <div className="text-xs tabular-nums text-muted-foreground">{effect}</div>}
              </li>
            );
          })}
        </ul>

        {error && (
          <div className="flex items-start gap-2 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300" role="alert">
            <AlertOctagon className="mt-px h-4 w-4 shrink-0" aria-hidden />
            <span className="min-w-0 break-words">{error}</span>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            {t("common.cancel")}
          </Button>
          <Button onClick={onConfirm} disabled={saving || changes.length === 0}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {t("evalSettings.confirm.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
